import { memo, useEffect, useState, useRef } from 'react'
import { GS, buildImage, downloadPicture } from 'back-end-api'
import LoaderMini from 'components/common/loaders/mini'
import { useTranslation } from 'services/Translation'
import { validateImageFile } from 'utils/imageUpload'

import { Container, ProfileSection, ProfilePicture, UserInfo, UserName, UserRole } from './Styles'

const SPECIALITY_LABELS = {
  GP: 'General Practitioner',
  CVHM: 'CVHM Specialist',
  emergency: 'Emergency Medicine',
  other: 'Doctor',
}

const texts = (getText) => (key) =>
  ({
    doctor: getText('Doctor', 'Account_Header'),
    loading: getText('Loading...', 'Account_Header'),
    uploadPhoto: getText('Upload photo', 'Account_Header'),
    uploading: getText('Uploading...', 'Account_Header'),
  }[key])

const ProfilePictureStatus = {
  Unavailable: 0,
  Available: 1,
  Loading: 2,
}

const Header = () => {
  const { t } = useTranslation(texts)
  const accountResp = GS.useOwnAccount({ refreshInterval: 0 })
  const [profilePicture, setProfilePicture] = useState(null)
  const [profilePictureStatus, setProfilePictureStatus] = useState(ProfilePictureStatus.Unavailable)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef(null)

  useEffect(() => {
    const fetchAndDecodeImage = async () => {
      if (accountResp?.data?.profile?.picture_url) {
        setProfilePictureStatus(ProfilePictureStatus.Loading)
        try {
          const image = await downloadPicture(accountResp.data.profile.picture_url)
          setProfilePicture(image)
          setProfilePictureStatus(
            image ? ProfilePictureStatus.Available : ProfilePictureStatus.Unavailable
          )
        } catch (error) {
          console.error('Error loading profile picture:', error)
          setProfilePictureStatus(ProfilePictureStatus.Unavailable)
        }
      } else {
        setProfilePictureStatus(ProfilePictureStatus.Unavailable)
      }
    }

    fetchAndDecodeImage()
    // Depend only on the stable picture_url string. Depending on the whole
    // accountResp object caused an infinite request loop because SWR returns a
    // new object reference on every render/revalidation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountResp?.data?.profile?.picture_url])

  const handleProfilePictureClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click()
    }
  }

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate type and size (shared limits kept in sync with the backend).
    const validationError = validateImageFile(file)
    if (validationError) {
      alert(validationError)
      return
    }

    setIsUploading(true)
    try {
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          const base64Data = e.target.result
          const extension = file.name.split('.').pop() || 'png'
          const image = buildImage(file.type, base64Data.split(',')[1], file.name)

          const result = await GS.updateOwnPicture(image)
          if (result === GS.updateOwnPictureStatus.PictureUploaded) {
            // Show the uploaded image immediately
            setProfilePicture(base64Data)
            setProfilePictureStatus(ProfilePictureStatus.Available)
          } else {
            alert('Failed to upload profile picture. Please try again.')
          }
        } catch (error) {
          console.error('Error uploading profile picture:', error)
          alert('Error uploading profile picture. Please try again.')
        } finally {
          setIsUploading(false)
        }
      }
      reader.readAsDataURL(file)
    } catch (error) {
      console.error('Error reading file:', error)
      setIsUploading(false)
    }

    // Reset the input so the same file can be selected again
    fileInputRef.current.value = ''
  }

  const profile = accountResp?.data?.profile
  const specialityLabel = profile?.speciality
    ? SPECIALITY_LABELS[profile.speciality] || profile.speciality
    : t('doctor')

  return (
    <Container>
      <ProfileSection>
        <ProfilePicture
          onClick={handleProfilePictureClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleProfilePictureClick()
            }
          }}
          aria-label={t('uploadPhoto')}
          style={{ cursor: isUploading ? 'wait' : 'pointer' }}
        >
          {(() => {
            if (isUploading) {
              return <LoaderMini center />
            }
            switch (profilePictureStatus) {
              case ProfilePictureStatus.Loading:
                return <LoaderMini center />
              case ProfilePictureStatus.Available:
                return (
                  <>
                    <img
                      src={profilePicture}
                      alt="Profile"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '50%',
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '35%',
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
                        borderRadius: '0 0 50% 50%',
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                        paddingBottom: '6px',
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                      }}
                      className="profile-pic-overlay"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="13" r="4" stroke="white" strokeWidth="2" fill="none"/>
                      </svg>
                    </div>
                  </>
                )
              case ProfilePictureStatus.Unavailable:
              default:
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.7)' }}>{t('uploadPhoto')}</span>
                  </div>
                )
            }
          })()}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            aria-hidden="true"
          />
        </ProfilePicture>
        
        <UserInfo>
          <UserName>
            {profile?.first_name || 'Mandy'} {profile?.last_name || 'van Halen'}
          </UserName>
          
          <UserRole>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{specialityLabel}</span>
          </UserRole>
        </UserInfo>
      </ProfileSection>
    </Container>
  )
}

Header.whyDidYouRender = true

export default memo(Header)